import { Location } from "@angular/common";
import { Injectable, OnDestroy } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { LoginFortyDaysReviewDialogService, LoginYearlyReviewService, LoginErrorService } from "@obg/common.login";
import { RealTimeNotificationsConfig } from "@obg/common.realtime-notifications";
import {
	AgeVerificationDialogService,
	CampaignService,
	CookieNoticeService,
	DepositLimitReconfirmationService,
	GeofencingAlertService,
	IauiBettingLimitService,
	IauiVerificationService,
	RealityCheckService,
	SessionExpiredAlertService,
	SessionLimitExpiredAlertContainer,
	SessionLimitExpiredAlertContainerV2,
	SessionTimeLimitService,
} from "@obg/mat.ui.common";
import { ZhhSessionLimitGuardService } from "@obg/mat.ui.common.responsible-gaming";
import { AlertButtonColor, AlertButtonStyle } from "@obg/mat.ui.core";
import { AwsGeofencingService, HampiService } from "@obg/ngx.common";
import {
	AbstractTranslatableAlertService,
	CORE_ROUTE,
	CustomerManagementSelector,
	MessageSelector,
	MessageType,
	SessionAction,
	SessionSelector,
	StatusCodeService,
} from "@obg/ngx.core";
import { CoreContentState, TranslationGroup } from "@obg/ngx.core.content";
import { StatusCodes } from "@obg/ngx.global";
import { PlatformService } from "@obg/ngx.global.evo";
import { Route, RouteAction, RouteSelector } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { BehaviorSubject, EMPTY, interval, merge, Observable, Subscription } from "rxjs";
import {
	distinctUntilChanged,
	exhaustMap,
	filter,
	mergeMap,
	switchMap,
	take,
	takeWhile,
	tap,
	withLatestFrom,
} from "rxjs/operators";

@Injectable({
	providedIn: "root",
})
export class AppMessagesBootstrap implements OnDestroy {
	private route$ = new BehaviorSubject<Route | undefined>(undefined);
	private data$$: Subscription | undefined;

	constructor(
		private store: Store<CoreContentState>,
		private sessionSelector: SessionSelector,
		private sessionAction: SessionAction,
		private messageSelector: MessageSelector,
		private location: Location,
		private platformService: PlatformService,
		private realityCheckService: RealityCheckService,
		private campaignService: CampaignService,
		private cookieNoticeService: CookieNoticeService,
		private translatableAlertService: AbstractTranslatableAlertService,
		private depositLimitReconfirmationService: DepositLimitReconfirmationService,
		private sessionTimeLimitService: SessionTimeLimitService,
		private iauiVerificationService: IauiVerificationService,
		private loginErrorService: LoginErrorService,
		private hampiService: HampiService,
		private sessionExpiredAlertService: SessionExpiredAlertService,
		private geofencingAlertService: GeofencingAlertService,
		private realTimeNotificationsConfig: RealTimeNotificationsConfig,
		private statusCodeService: StatusCodeService,
		private iauiBettingLimitService: IauiBettingLimitService,
		private awsGeofencingService: AwsGeofencingService,
		private zhhSessionLimitGuardService: ZhhSessionLimitGuardService,
		private loginFortyDaysReviewDialogService: LoginFortyDaysReviewDialogService,
		private loginYearlyReviewService: LoginYearlyReviewService,
		private ageVerificationDialogService: AgeVerificationDialogService
	) {}

	/**
	 * This should be called once.
	 */
	init() {
		// some routes are used as plugins within 3rd party applications
		// UX should be very limited there, like no popups, no messages etc
		const routesWithoutMessages = [
			CORE_ROUTE.pokerLogin,
			CORE_ROUTE.commonPokerLogin,
			CORE_ROUTE.pokerCasinoGallery,
		];

		const route$ = this.store.select(RouteSelector.getCurrent).pipe(
			ignoreNil(),
			takeWhile((route) => !routesWithoutMessages.includes(route.name)),
			tap((route) => this.route$.next(route))
		);

		const init$ = this.route$.pipe(
			ignoreNil(),
			take(1),
			mergeMap(() => this.init$())
		);

		this.data$$ = merge(route$, init$).subscribe();
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}

	private init$() {
		const sessionInvalid$ = this.store.pipe(
			select(this.sessionSelector.isSessionInvalid()),
			distinctUntilChanged(),
			filter((isInvalid) => isInvalid),
			withLatestFrom(this.store.select(this.sessionSelector.getSessionErrorStatusMessage())),
			tap(() => this.store.dispatch(this.sessionAction.logout())),
			exhaustMap(([, errorMessage]) => {
				if (!this.isClientSide) {
					return EMPTY;
				}

				return this.showSessionInvalidAlert$(errorMessage).pipe(take(1));
			})
		);

		const sessionTimeLimitExpired$ = this.store.pipe(
			select(this.sessionSelector.isSessionLimitExpired()),
			filter((isExpired) => isExpired),
			withLatestFrom(this.store.select(this.sessionSelector.getNextTimeLogin())),
			exhaustMap(([, nextTimeLogin]) => {
				if (!this.realTimeNotificationsConfig.getSessionLimitReachedEnabled()) {
					return EMPTY;
				}

				return new Observable((observer) => {
					if (this.realTimeNotificationsConfig.getSessionLimitV2Enabled()) {
						this.showSessionLimitExpiredAlertV2(nextTimeLogin);
						observer.complete();

						return;
					}

					this.showSessionLimitExpiredAlert(nextTimeLogin);
					observer.complete();
				});
			})
		);

		const currentRoute$ = this.store.select(RouteSelector.getCurrent).pipe(
			filter((route) => !!route),
			tap((route) => this.route$.next(route))
		);

		const cookieNoticePanelClasses = {
			cookieNotice: ["large-width"],
			gdprNotice: ["large-width", "raised-button"],
		};

		const cookieNoticeSnackbar$ = this.platformService.forClient(() =>
			this.store.select(RouteSelector.getCurrent).pipe(
				filter((route) => !!route),
				take(1),
				switchMap(() => this.cookieNoticeService.initCookieSnackbar(cookieNoticePanelClasses))
			)
		);

		const thirdPartyCookieSettings$ = this.store.select(RouteSelector.getCurrent).pipe(
			filter((route) => !!route && !!route.queryParams && route.queryParams.thirdPartyCookieSystem),
			switchMap(() =>
				interval(2000).pipe(
					take(2),
					filter(() => typeof OneTrust !== "undefined"),
					take(1),
					tap(() => OneTrust.ToggleInfoDisplay())
				)
			)
		);

		const unsubscribeFromNewsLetter$ = this.store
			.select(CustomerManagementSelector.hasUnsubscribedFromNewsletter)
			.pipe(
				filter((isUnsubscribed) => !!isUnsubscribed),
				tap(() => this.showNewsLetterUnsubscribeAlert())
			);

		const internalError$ = this.store.pipe(
			select(this.messageSelector.isFatalError()),
			filter((response) => response),
			switchMap(() => this.store.select(RouteSelector.getCurrent).pipe(ignoreNil(), take(1))),
			withLatestFrom(
				this.store.select(
					this.messageSelector.getFirst({
						messageType: MessageType.Fatal,
					})
				)
			),
			tap(([route, message]) => {
				this.store.dispatch(
					RouteAction.navigate(CORE_ROUTE.internalError, undefined, { skipLocationChange: true })
				);

				this.statusCodeService.setStatusCode(StatusCodes.InternalServerError, JSON.stringify(message));

				if (this.platformService.isBrowser) {
					// todo: remove when solved: https://github.com/angular/angular/issues/16981
					const pathWithoutMarket = route.pathName.replace(/(\/[^/]*\b\/*?)/, "");
					setTimeout(() => this.location.replaceState(pathWithoutMarket), 0);
				}
			})
		);

		const successfulJoin$ = this.campaignService.successfulJoin$();
		const failedJoin$ = this.campaignService.failedJoin$();
		const successfulOptIn$ = this.campaignService.successfulOptIn$();
		const failedOptIn$ = this.campaignService.failedOptIn$();
		const loginSelfExcludedMessage$ = this.loginErrorService.selfExcludedLoginMessage$();

		return merge(
			this.depositLimitReconfirmationService.initialize$(),
			this.realityCheckService.initialize$(),
			this.sessionTimeLimitService.initialize$(),
			this.iauiVerificationService.initialize$(),
			this.hampiService.initialize$(),
			this.awsGeofencingService.initialize$(),
			this.sessionExpiredAlertService.initialize$(),
			this.geofencingAlertService.initialize$(),
			this.iauiBettingLimitService.initialize$(),
			this.zhhSessionLimitGuardService.initialize$(),
			this.loginFortyDaysReviewDialogService.initialize$(),
			this.loginYearlyReviewService.initialize$(),
			this.ageVerificationDialogService.initialize$(),
			sessionTimeLimitExpired$,
			sessionInvalid$,
			currentRoute$,
			successfulJoin$,
			failedJoin$,
			successfulOptIn$,
			failedOptIn$,
			cookieNoticeSnackbar$,
			unsubscribeFromNewsLetter$,
			internalError$,
			thirdPartyCookieSettings$,
			loginSelfExcludedMessage$
		);
	}

	private showSessionInvalidAlert$(errorMessage: string | undefined) {
		return this.translatableAlertService.open$(
			{
				title: "user-summary.session-invalid-title",
				message: errorMessage
					? `user-summary.${errorMessage.toLowerCase()}`
					: "user-summary.session-invalid-message",
				hideClose: true,
				dialogConfig: {
					disableClose: true,
				},
				mustBeInteracted: true,
				buttons: [
					{
						color: AlertButtonColor.Primary,
						text: "general.close",
					},
					{
						text: "user-summary.login",
						handler: () => this.openHomeLoginDialog(),
					},
				],
			},
			[]
		);
	}

	private showSessionLimitExpiredAlert(nextTimeLogin?: string) {
		this.translatableAlertService.open(
			{
				title: nextTimeLogin
					? "user-summary.session-limit-expired-next-time-login-title"
					: "user-summary.session-invalid-title",
				messageComponent: SessionLimitExpiredAlertContainer,
				messageComponentInit: (c: SessionLimitExpiredAlertContainer) => (c.nextTimeLogin = nextTimeLogin),
				hideClose: true,
				dialogConfig: {
					disableClose: true,
				},
				mustBeInteracted: true,
				buttons: [{ text: "general.close" }],
				prefixJurisdiction: true,
			},
			[]
		);
	}

	showSessionLimitExpiredAlertV2(nextTimeLogin?: string) {
		this.translatableAlertService.open(
			{
				title: undefined,
				messageComponent: SessionLimitExpiredAlertContainerV2,
				messageComponentInit: (c: SessionLimitExpiredAlertContainerV2) => (c.nextTimeLogin = nextTimeLogin),
				hideClose: true,
				dialogConfig: {
					disableClose: true,
					panelClass: "session-limit-expired-alert-v2",
				},
				mustBeInteracted: true,
				buttons: [{ text: "general.close", style: AlertButtonStyle.Stroked, color: AlertButtonColor.Primary }],
				prefixJurisdiction: true,
			},
			[]
		);
	}

	private openHomeLoginDialog() {
		const route = this.route$.getValue();
		if (!route) {
			return;
		}

		const queryParams = { returnUrl: route.externalUrl, action: CORE_ROUTE.auth.login };
		this.store.dispatch(RouteAction.navigate(CORE_ROUTE.home, undefined, { queryParams }));
	}

	private showNewsLetterUnsubscribeAlert() {
		this.translatableAlertService.open(
			{
				title: "newsletter-unsubscribe.title",
				message: "newsletter-unsubscribe.message",
				buttons: [
					{
						color: AlertButtonColor.Primary,
						text: "general.ok",
					},
				],
			},
			[TranslationGroup.General]
		);
	}

	get isClientSide(): boolean {
		return this.platformService.isBrowser;
	}
}
